import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { PendingPaymentPoller } from '../../../components/PendingPaymentPoller';
import { useParkingaboAuthedPathGeneration } from '../../RouteUtils';
import { PaymentMethodType } from '../../../components/forms/PaymentMethodForm';
import { useParkingaboServerWrite } from '../../../api/ParkingaboApi';
import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';

export function PaymentMethodCheckRoute({
    paymentMethodType,
}: {
    paymentMethodType: PaymentMethodType;
}) {
    const { transactionId } = useParams<{ transactionId: string }>();
    const { user, refetchUser } = useCustomerContext();
    const searchParams = new URLSearchParams(window.location.search);
    const generateAuthedParkingaboPath = useParkingaboAuthedPathGeneration();
    const status = searchParams.get('status');
    const datatransTrxId = searchParams.get('datatransTrxId');
    const redirectOnCanceled = ((): string => {
        switch (paymentMethodType) {
            case PaymentMethodType.REGISTER:
                return generateAuthedParkingaboPath('payment/register/aborted');
            case PaymentMethodType.ONBOARDING:
                return getOnboardingPaymentCancelledPathByAppMode(
                    generateAuthedParkingaboPath,
                    user.role.paymentMode,
                );
        }
    })();
    const navigate = useNavigate();

    const [processDatatransRedirectState, initiateDatatransRedirectProcess] =
        useParkingaboServerWrite<{ datatransTransactionId: string }, unknown>(
            ({ datatransTransactionId }) => ({
                url: `user/self/payment/datatrans/${datatransTransactionId}/process-redirect`,
            }),
        );

    useEffect(() => {
        if (
            datatransTrxId !== null &&
            processDatatransRedirectState.status ===
                RequestStatus.NEVER_EXECUTED
        ) {
            initiateDatatransRedirectProcess({
                datatransTransactionId: datatransTrxId,
            });
        }
    }, [
        datatransTrxId,
        processDatatransRedirectState.status,
        initiateDatatransRedirectProcess,
    ]);

    if (!transactionId) {
        return null;
    }

    return (
        <PendingPaymentPoller
            pendingPaymentId={transactionId}
            refetchUser={refetchUser}
            cancelling={status === 'cancel'}
            twintPairingAborted={status === 'aborted'}
            onboarding={paymentMethodType === PaymentMethodType.ONBOARDING}
            onPaymentCanceled={() => {
                navigate(redirectOnCanceled);
            }}
        />
    );
}

function getOnboardingPaymentCancelledPathByAppMode(
    generateAuthedParkingaboPath: (path: string) => string,
    paymentMode: ParkingaboCustomerPaymentMode,
): string {
    switch (paymentMode) {
        case ParkingaboCustomerPaymentMode.APP_PAYMENT:
            return generateAuthedParkingaboPath('onboarding/payment/aborted');
        case ParkingaboCustomerPaymentMode.INDIVIDUAL_PAYMENT:
        case ParkingaboCustomerPaymentMode.COUNTER:
            return '/not-found';
    }
}
